import { apiClient } from './client';
import type {
  Sport,
  Organization,
  Venue,
  Team,
  Player,
  Official,
  Tournament,
  Registration,
  Match,
  MatchEvent,
  StandingTable,
  Bracket,
  PlayerStats,
  LeaderboardPlayers,
  TournamentResults,
  PaginatedResponse,
  LoginResponse,
  RefreshRequest,
  AuthEmailRequest,
  AuthEmailConfirmRequest,
  AuthEmailConfirmResponse,
  AuthWebAuthnOptions,
  AuthWebAuthnFinishRequest,
  AuthWebAuthnCredential,
  CreateTournamentRequest,
  UpdateTournamentRequest,
  TournamentStage,
  TournamentStageStatus,
  TournamentStageType,
  StageSettings,
  CreateMatchRequest,
  CreateMatchEventRequest,
  ReportResultRequest,
  ScheduleGenerationRequest,
  ScheduleGenerationResponse,
  SchedulePreviewResponse,
  ScheduleSaveRequest,
  SportType,
  SportSettings,
} from '@/types';

const asNumber = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined;
const asBool = (v: unknown): boolean | undefined =>
  typeof v === 'boolean' ? v : undefined;
const asString = (v: unknown): string | undefined =>
  typeof v === 'string' ? v : undefined;
const readPointRuleNumber = (points: Record<string, unknown> | undefined, key: string): number | undefined =>
  asNumber(points?.[key]);
const readPointRuleBool = (points: Record<string, unknown> | undefined, key: string): boolean | undefined =>
  asBool(points?.[key]);
const readFirstNumber = (
  srcA: Record<string, unknown> | undefined,
  srcB: Record<string, unknown> | undefined,
  keys: string[],
): number | undefined => {
  for (const key of keys) {
    const v = asNumber(srcA?.[key] ?? srcB?.[key]);
    if (v != null) return v;
  }
  return undefined;
};
const readFirstBool = (
  srcA: Record<string, unknown> | undefined,
  srcB: Record<string, unknown> | undefined,
  keys: string[],
): boolean | undefined => {
  for (const key of keys) {
    const v = asBool(srcA?.[key] ?? srcB?.[key]);
    if (v != null) return v;
  }
  return undefined;
};
const readFirstString = (
  srcA: Record<string, unknown> | undefined,
  srcB: Record<string, unknown> | undefined,
  keys: string[],
): string | undefined => {
  for (const key of keys) {
    const v = asString(srcA?.[key] ?? srcB?.[key]);
    if (v != null) return v;
  }
  return undefined;
};

const normalizeEventPayload = <T extends { actorId?: string | null; targetId?: string | null }>(data: T): T => {
  const actorId = typeof data.actorId === 'string' && data.actorId.trim() ? data.actorId : undefined;
  const targetId = typeof data.targetId === 'string' && data.targetId.trim() ? data.targetId : undefined;

  return {
    ...data,
    actorId,
    targetId,
  };
};
const asRecord = (v: unknown): Record<string, unknown> | undefined =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;
const poolToCode = (pool: 'short_course_25' | 'long_course_50' | 'open_water'): number => {
  switch (pool) {
    case 'short_course_25':
      return 25;
    case 'long_course_50':
      return 50;
    case 'open_water':
      return 0;
  }
};
const codeToPool = (code?: number): 'short_course_25' | 'long_course_50' | 'open_water' | undefined => {
  if (code === 25) return 'short_course_25';
  if (code === 50) return 'long_course_50';
  if (code === 0) return 'open_water';
  return undefined;
};

const toSportSettings = (sportType: SportType, settings?: unknown): SportSettings => {
  if (!settings || typeof settings !== 'object') return { sportType };

  const src = settings as {
    pointsRules?: Record<string, unknown>;
    tieBreakers?: string[];
    matchRules?: Record<string, unknown>;
  };
  const points = src.pointsRules;
  const rules = src.matchRules;
  const uiSnapshot = asRecord(points?.__uiSettings);

  const base = {
    sportType,
    pointsWin: asNumber(points?.win),
    pointsDraw: asNumber(points?.draw),
    pointsLoss: asNumber(points?.loss),
    tieBreakers: src.tieBreakers,
  };

  switch (sportType) {
    case 'football':
      return {
        ...base,
        ...(uiSnapshot ?? {}),
        matchDurationMin: asNumber(rules?.periodDurationMin),
        extraTime: readFirstBool(points, rules, ['extraTime', 'overtime']),
        penaltyShootout: readFirstBool(points, rules, ['penaltyShootout']),
        playersOnField: readPointRuleNumber(points, 'playersOnField'),
        squadSize: readPointRuleNumber(points, 'squadSize'),
        substitutionsPerMatch: readPointRuleNumber(points, 'substitutionsPerMatch'),
        offside: readPointRuleBool(points, 'offside'),
      };
    case 'hockey':
      return {
        ...base,
        ...(uiSnapshot ?? {}),
        periods: asNumber(rules?.periods),
        periodDurationMin: asNumber(rules?.periodDurationMin),
        overtime: asBool(rules?.overtime),
        shootout: asBool(rules?.penaltyShootout),
        playersOnIce: readPointRuleNumber(points, 'playersOnIce'),
        pointsWinOT: readFirstNumber(points, rules, ['winOT', 'winOt', 'pointsWinOT']),
        pointsLossOT: readFirstNumber(points, rules, ['lossOT', 'lossOt', 'pointsLossOT']),
        overtimeDurationMin: readPointRuleNumber(points, 'overtimeDurationMin'),
      };
    case 'basketball':
      return {
        ...base,
        ...(uiSnapshot ?? {}),
        quarters: asNumber(rules?.periods),
        quarterDurationMin: asNumber(rules?.periodDurationMin),
        playersOnCourt: readPointRuleNumber(points, 'playersOnCourt'),
        overtimeDurationMin: readPointRuleNumber(points, 'overtimeDurationMin'),
        foulLimit: readPointRuleNumber(points, 'foulLimit'),
      };
    case 'tennis':
      return {
        sportType,
        ...(uiSnapshot ?? {}),
        pointsWin: base.pointsWin,
        pointsLoss: base.pointsLoss,
        tieBreakers: base.tieBreakers,
        sets: asNumber(rules?.bestOf),
        finalSetTiebreak: typeof rules?.setTieBreak === 'boolean' ? rules.setTieBreak : undefined,
        tiebreakAt: readPointRuleNumber(points, 'tiebreakAt'),
        superTiebreak: readFirstBool(points, rules, ['superTiebreak']),
        noAd: readPointRuleBool(points, 'noAd'),
      };
    case 'volleyball':
      return {
        sportType,
        ...(uiSnapshot ?? {}),
        pointsWin: base.pointsWin,
        pointsLoss: base.pointsLoss,
        tieBreakers: base.tieBreakers,
        sets: asNumber(rules?.bestOf),
        pointsToWinSet: readPointRuleNumber(points, 'pointsToWinSet'),
        finalSetPoints: readPointRuleNumber(points, 'finalSetPoints'),
        pointsWinAway: readFirstNumber(points, rules, ['winAway', 'pointsWinAway']),
        pointsLossAway: readFirstNumber(points, rules, ['lossAway', 'pointsLossAway']),
      };
    case 'handball':
      return {
        ...base,
        ...(uiSnapshot ?? {}),
        playersOnField: readPointRuleNumber(points, 'playersOnField'),
        periods: asNumber(rules?.periods),
        periodDurationMin: asNumber(rules?.periodDurationMin),
        overtime: asBool(rules?.overtime),
        shootout: asBool(rules?.penaltyShootout),
      };
    case 'padel':
      return {
        sportType,
        ...(uiSnapshot ?? {}),
        pointsWin: base.pointsWin,
        pointsLoss: base.pointsLoss,
        tieBreakers: base.tieBreakers,
        sets: asNumber(rules?.bestOf),
        tiebreakAt: readPointRuleNumber(points, 'tiebreakAt'),
        superTiebreak: readFirstBool(points, rules, ['superTiebreak']),
        goldenPoint: readPointRuleBool(points, 'goldenPoint'),
      };
    case 'swimming':
      const poolFromString = readFirstString(points, rules, ['pool', 'poolType']);
      const poolFromCode = codeToPool(readFirstNumber(points, rules, ['poolCode', 'poolMeters']));
      return {
        sportType,
        ...(uiSnapshot ?? {}),
        tieBreakers: base.tieBreakers as ('time' | 'qualifying_time' | 'drawing_lots')[] | undefined,
        pool: (poolFromString as 'short_course_25' | 'long_course_50' | 'open_water' | undefined) ?? poolFromCode,
      };
    default:
      return {
        ...base,
        ...(uiSnapshot ?? {}),
      };
  }
};

const toApiTournamentSettings = (
  settings: SportSettings,
): Record<string, unknown> => {
  const pointsRules: Record<string, unknown> = {};
  const matchRules: Record<string, unknown> = {};

  if ('pointsWin' in settings && settings.pointsWin != null) pointsRules.win = settings.pointsWin;
  if ('pointsDraw' in settings && settings.pointsDraw != null) pointsRules.draw = settings.pointsDraw;
  if ('pointsLoss' in settings && settings.pointsLoss != null) pointsRules.loss = settings.pointsLoss;
  // Store full UI snapshot to preserve sport-specific fields across schema drifts.
  const { sportType, ...uiSettings } = settings as unknown as Record<string, unknown>;
  pointsRules.__uiSettings = uiSettings;

  switch (settings.sportType) {
    case 'football':
      if ('matchDurationMin' in settings && settings.matchDurationMin != null) matchRules.periodDurationMin = settings.matchDurationMin;
      if ('extraTime' in settings && settings.extraTime != null) matchRules.overtime = settings.extraTime;
      if ('penaltyShootout' in settings && settings.penaltyShootout != null) matchRules.penaltyShootout = settings.penaltyShootout;
      if ('playersOnField' in settings && settings.playersOnField != null) pointsRules.playersOnField = settings.playersOnField;
      if ('squadSize' in settings && settings.squadSize != null) pointsRules.squadSize = settings.squadSize;
      if ('substitutionsPerMatch' in settings && settings.substitutionsPerMatch != null) {
        pointsRules.substitutionsPerMatch = settings.substitutionsPerMatch;
      }
      if ('offside' in settings && settings.offside != null) pointsRules.offside = settings.offside;
      break;
    case 'hockey':
      if ('periods' in settings && settings.periods != null) matchRules.periods = settings.periods;
      if ('periodDurationMin' in settings && settings.periodDurationMin != null) matchRules.periodDurationMin = settings.periodDurationMin;
      if ('overtime' in settings && settings.overtime != null) matchRules.overtime = settings.overtime;
      if ('shootout' in settings && settings.shootout != null) matchRules.penaltyShootout = settings.shootout;
      if ('playersOnIce' in settings && settings.playersOnIce != null) pointsRules.playersOnIce = settings.playersOnIce;
      if ('pointsWinOT' in settings && settings.pointsWinOT != null) pointsRules.winOt = settings.pointsWinOT;
      if ('pointsWinOT' in settings && settings.pointsWinOT != null) pointsRules.winOT = settings.pointsWinOT;
      if ('pointsLossOT' in settings && settings.pointsLossOT != null) pointsRules.lossOt = settings.pointsLossOT;
      if ('pointsLossOT' in settings && settings.pointsLossOT != null) pointsRules.lossOT = settings.pointsLossOT;
      if ('overtimeDurationMin' in settings && settings.overtimeDurationMin != null) {
        pointsRules.overtimeDurationMin = settings.overtimeDurationMin;
      }
      break;
    case 'basketball':
      if ('quarters' in settings && settings.quarters != null) matchRules.periods = settings.quarters;
      if ('quarterDurationMin' in settings && settings.quarterDurationMin != null) {
        matchRules.periodDurationMin = settings.quarterDurationMin;
      }
      if ('playersOnCourt' in settings && settings.playersOnCourt != null) {
        pointsRules.playersOnCourt = settings.playersOnCourt;
      }
      if ('overtimeDurationMin' in settings && settings.overtimeDurationMin != null) {
        pointsRules.overtimeDurationMin = settings.overtimeDurationMin;
      }
      if ('foulLimit' in settings && settings.foulLimit != null) {
        pointsRules.foulLimit = settings.foulLimit;
      }
      break;
    case 'tennis':
      if ('sets' in settings && settings.sets != null) matchRules.bestOf = settings.sets;
      if ('finalSetTiebreak' in settings && settings.finalSetTiebreak != null) {
        matchRules.setTieBreak = settings.finalSetTiebreak;
      }
      if ('tiebreakAt' in settings && settings.tiebreakAt != null) pointsRules.tiebreakAt = settings.tiebreakAt;
      if ('superTiebreak' in settings && settings.superTiebreak != null) pointsRules.superTiebreak = settings.superTiebreak;
      if ('noAd' in settings && settings.noAd != null) pointsRules.noAd = settings.noAd;
      break;
    case 'volleyball':
      if ('sets' in settings && settings.sets != null) matchRules.bestOf = settings.sets;
      if ('pointsToWinSet' in settings && settings.pointsToWinSet != null) pointsRules.pointsToWinSet = settings.pointsToWinSet;
      if ('finalSetPoints' in settings && settings.finalSetPoints != null) pointsRules.finalSetPoints = settings.finalSetPoints;
      if ('pointsWinAway' in settings && settings.pointsWinAway != null) pointsRules.winAway = settings.pointsWinAway;
      if ('pointsWinAway' in settings && settings.pointsWinAway != null) pointsRules.pointsWinAway = settings.pointsWinAway;
      if ('pointsLossAway' in settings && settings.pointsLossAway != null) pointsRules.lossAway = settings.pointsLossAway;
      if ('pointsLossAway' in settings && settings.pointsLossAway != null) pointsRules.pointsLossAway = settings.pointsLossAway;
      break;
    case 'handball':
      if ('playersOnField' in settings && settings.playersOnField != null) pointsRules.playersOnField = settings.playersOnField;
      if ('periods' in settings && settings.periods != null) matchRules.periods = settings.periods;
      if ('periodDurationMin' in settings && settings.periodDurationMin != null) matchRules.periodDurationMin = settings.periodDurationMin;
      if ('overtime' in settings && settings.overtime != null) matchRules.overtime = settings.overtime;
      if ('shootout' in settings && settings.shootout != null) matchRules.penaltyShootout = settings.shootout;
      break;
    case 'padel':
      if ('sets' in settings && settings.sets != null) matchRules.bestOf = settings.sets;
      if ('tiebreakAt' in settings && settings.tiebreakAt != null) pointsRules.tiebreakAt = settings.tiebreakAt;
      if ('superTiebreak' in settings && settings.superTiebreak != null) pointsRules.superTiebreak = settings.superTiebreak;
      if ('goldenPoint' in settings && settings.goldenPoint != null) pointsRules.goldenPoint = settings.goldenPoint;
      break;
    case 'swimming':
      if ('pool' in settings && settings.pool != null) {
        // Keep swimming pool as numeric code to avoid backend JSONB failures on raw string additionalProperties.
        pointsRules.poolCode = poolToCode(settings.pool);
        pointsRules.poolMeters = poolToCode(settings.pool);
      }
      break;
    default:
      break;
  }

  return {
    pointsRules: Object.keys(pointsRules).length ? pointsRules : undefined,
    tieBreakers: 'tieBreakers' in settings ? settings.tieBreakers : undefined,
    matchRules: Object.keys(matchRules).length ? matchRules : undefined,
  };
};

// ============ AUTH SERVICE ============
export const authService = {
  emailStart: (data: AuthEmailRequest) => apiClient.post<void>('/auth/email/start', data),
  emailConfirm: (data: AuthEmailConfirmRequest) =>
    apiClient.post<AuthEmailConfirmResponse>('/auth/email/confirm', data),
  devLogin: (data: AuthEmailRequest) => apiClient.post<LoginResponse>('/auth/dev/login', data),
  refresh: (data: RefreshRequest) => apiClient.post<LoginResponse>('/auth/refresh', data),
  logout: (refreshToken: string) => apiClient.post<void>('/auth/logout', { refreshToken }),
  webauthnLoginBegin: (data: { email?: string } = {}) =>
    apiClient.post<AuthWebAuthnOptions>('/auth/webauthn/login/begin', data),
  webauthnLoginFinish: (data: AuthWebAuthnFinishRequest) =>
    apiClient.post<LoginResponse>('/auth/webauthn/login/finish', data),
  webauthnRegisterBegin: () =>
    apiClient.post<AuthWebAuthnOptions>('/auth/webauthn/register/begin', {}),
  webauthnRegisterFinish: (data: AuthWebAuthnFinishRequest) =>
    apiClient.post<AuthWebAuthnCredential>('/auth/webauthn/register/finish', data),
  listPasskeys: () => apiClient.get<AuthWebAuthnCredential[]>('/auth/webauthn/credentials'),
  renamePasskey: (id: string, friendlyName: string) =>
    apiClient.patch<AuthWebAuthnCredential>(`/auth/webauthn/credentials/${id}`, { friendlyName }),
  deletePasskey: (id: string) => apiClient.delete<void>(`/auth/webauthn/credentials/${id}`),
};

export const usersService = {
  getRoles: (userId: string) =>
    apiClient.get<{
      user_id: string;
      organizations: Record<string, string[]>;
      tournaments: Record<string, string[]>;
      teams: Record<string, string[]>;
    }>(`/users/${userId}/roles`),
};

// ============ SPORTS SERVICE ============
export const sportsService = {
  // Get all sports
  getAll: () => apiClient.get<Sport[]>('/sports'),
  
  // Get sport by ID
  getById: (id: string) => apiClient.get<Sport>(`/sports/${id}`),
  
  // Create new sport
  create: (data: { code: SportType; name: string; active?: boolean; rules?: Record<string, unknown> }) => 
    apiClient.post<Sport>('/sports', data),
  
  // Update sport
  update: (id: string, data: Partial<{ code: SportType; name: string; active: boolean; rules: Record<string, unknown> }>) => 
    apiClient.patch<Sport>(`/sports/${id}`, data),
  
  // Delete sport (soft delete)
  delete: (id: string) => apiClient.delete<void>(`/sports/${id}`),
  
  // Restore deleted sport
  restore: (id: string) => apiClient.post<Sport>(`/sports/${id}/restore`, {}),
};

// ============ ORGANIZATIONS SERVICE ============
export const organizationsService = {
  // Get all organizations with pagination
  getAll: (params?: { page?: number; limit?: number; active?: boolean; q?: string }) =>
    apiClient.get<PaginatedResponse<Organization>>('/organizations', { params }),
  
  // Get organization by ID
  getById: (id: string) => apiClient.get<Organization>(`/organizations/${id}`),
  
  // Create new organization
  create: (data: { name: string; country?: string; active?: boolean }) =>
    apiClient.post<Organization>('/organizations', data),
  
  // Update organization
  update: (id: string, data: Partial<{ name: string; country: string; active: boolean }>) =>
    apiClient.patch<Organization>(`/organizations/${id}`, data),
  
  // Delete organization (soft delete)
  delete: (id: string) => apiClient.delete<void>(`/organizations/${id}`),
  
  // Restore deleted organization
  restore: (id: string) => apiClient.post<Organization>(`/organizations/${id}/restore`, {}),

  // Get tournaments for a specific organization
  getTournaments: (id: string, params?: { page?: number; limit?: number; season?: string | null; sportType?: SportType; status?: string | null }) =>
    apiClient.get<PaginatedResponse<Tournament>>(`/organizations/${id}/tournaments`, { params }),
};

// ============ VENUES SERVICE ============
export const venuesService = {
  // Get all venues with pagination
  getAll: (params?: { page?: number; limit?: number; active?: boolean; q?: string }) =>
    apiClient.get<PaginatedResponse<Venue>>('/venues', { params }),
  
  // Get venue by ID
  getById: (id: string) => apiClient.get<Venue>(`/venues/${id}`),
  
  // Create new venue
  create: (data: { cityId?: string | null; name: string; timezone?: string; address: string; addressMeta: string; active?: boolean; sportTypes?: SportType[] }) =>
    apiClient.post<Venue>('/venues', data),
  
  // Update venue
  update: (id: string, data: Partial<{ cityId: string | null; name: string; timezone: string; address: string; addressMeta: string; active: boolean; sportTypes: SportType[] }>) =>
    apiClient.patch<Venue>(`/venues/${id}`, data),
  
  // Delete venue (soft delete)
  delete: (id: string) => apiClient.delete<void>(`/venues/${id}`),
  
  // Restore deleted venue
  restore: (id: string) => apiClient.post<Venue>(`/venues/${id}/restore`, {}),
};

// ============ TEAMS SERVICE ============
export const teamsService = {
  // Get all teams with pagination and filters
  getAll: (params?: { page?: number; limit?: number; active?: boolean; organizationId?: string; sportType?: SportType; q?: string }) =>
    apiClient.get<PaginatedResponse<Team>>('/teams', {
      params: params?.sportType
        ? {
            ...params,
            // Keep both spellings to support backends expecting either convention.
            sportType: params.sportType,
            sport_type: params.sportType,
          }
        : params,
    }),
  
  // Get team by ID
  getById: (id: string) => apiClient.get<Team>(`/teams/${id}`),
  
  // Create new team
  create: (data: { name: string; cityId?: string | null; sportType: SportType; organizationId?: string; active?: boolean; roster?: string[] }) =>
    apiClient.post<Team>('/teams', data),
  
  // Update team
  update: (id: string, data: Partial<{ name: string; cityId: string | null; active: boolean; roster: string[] }>) =>
    apiClient.patch<Team>(`/teams/${id}`, data),
  
  // Delete team (soft delete)
  delete: (id: string) => apiClient.delete<void>(`/teams/${id}`),
  
  // Restore deleted team
  restore: (id: string) => apiClient.post<Team>(`/teams/${id}/restore`, {}),
};

// ============ PLAYERS SERVICE ============
export const playersService = {
  // Get all players with pagination
  getAll: (params?: { page?: number; limit?: number; active?: boolean; q?: string }) =>
    apiClient.get<PaginatedResponse<Player>>('/players', { params }),
  
  // Get player by ID
  getById: (id: string) => apiClient.get<Player>(`/players/${id}`),
  
  // Create new player
  create: (data: { firstName: string; lastName?: string | null; gender?: 'male' | 'female' | null; birthDate?: string | null; active?: boolean }) =>
    apiClient.post<Player>('/players', data),
  
  // Update player
  update: (id: string, data: Partial<{ firstName: string; lastName: string | null; gender: 'male' | 'female' | null; birthDate: string | null; active: boolean }>) =>
    apiClient.patch<Player>(`/players/${id}`, data),
  
  // Delete player (soft delete)
  delete: (id: string) => apiClient.delete<void>(`/players/${id}`),
  
  // Restore deleted player
  restore: (id: string) => apiClient.post<Player>(`/players/${id}/restore`, {}),
  
  // Get player statistics
  getStats: (id: string, params?: { scope?: 'career' | 'season' | 'tournament'; season?: string; tournamentId?: string }) =>
    apiClient.get<PlayerStats>(`/players/${id}/stats`, { params }),
};

// ============ OFFICIALS SERVICE ============
export const officialsService = {
  // Get all officials with pagination
  getAll: (params?: { page?: number; limit?: number; active?: boolean; q?: string }) =>
    apiClient.get<PaginatedResponse<Official>>('/officials', { params }),
  
  // Get official by ID
  getById: (id: string) => apiClient.get<Official>(`/officials/${id}`),
  
  // Create new official
  create: (data: { name: string; cityId?: string | null; active?: boolean; roles?: ('referee' | 'umpire' | 'arbiter')[] }) =>
    apiClient.post<Official>('/officials', data),
  
  // Update official
  update: (id: string, data: Partial<{ name: string; cityId: string | null; active: boolean; roles: ('referee' | 'umpire' | 'arbiter')[] }>) =>
    apiClient.patch<Official>(`/officials/${id}`, data),
  
  // Delete official (soft delete)
  delete: (id: string) => apiClient.delete<void>(`/officials/${id}`),
  
  // Restore deleted official
  restore: (id: string) => apiClient.post<Official>(`/officials/${id}/restore`, {}),
};

// ============ TOURNAMENTS SERVICE ============
export const tournamentsService = {
  // Get all tournaments with pagination and filters
  getAll: (params?: { page?: number; limit?: number; organizationId?: string; season?: string | null; sportType?: SportType; status?: string | null; q?: string; mine?: boolean }) =>
    apiClient.get<PaginatedResponse<Tournament>>('/tournaments', { params }),
  
  // Get tournament by ID
  getById: (id: string) => apiClient.get<Tournament>(`/tournaments/${id}`),
  
  // Create new tournament
  create: (data: CreateTournamentRequest) => apiClient.post<Tournament>('/tournaments', data),
  
  // Update tournament
  update: (id: string, data: UpdateTournamentRequest) => apiClient.patch<Tournament>(`/tournaments/${id}`, data),
  
  // Delete tournament (soft delete)
  delete: (id: string) => apiClient.delete<void>(`/tournaments/${id}`),
  
  // Restore deleted tournament
  restore: (id: string) => apiClient.post<Tournament>(`/tournaments/${id}/restore`, {}),
  
  // ============ TOURNAMENT LIFECYCLE ============
  
  // Publish tournament
  publish: (id: string) => apiClient.post<void>(`/tournaments/${id}/publish`, {}),
  
  // Start tournament
  start: (id: string) => apiClient.post<void>(`/tournaments/${id}/start`, {}),
  
  // Complete tournament
  complete: (id: string) => apiClient.post<void>(`/tournaments/${id}/complete`, {}),
  
  // Cancel tournament
  cancel: (id: string) => apiClient.post<void>(`/tournaments/${id}/cancel`, {}),

  // ============ TOURNAMENT SETTINGS ============

  // Get sport-specific settings from tournament payload
  getSettings: async (id: string) => {
    const tournament = await apiClient.get<Tournament>(`/tournaments/${id}`);
    return toSportSettings(tournament.sportType, tournament.settings);
  },

  // Save sport-specific settings through tournament PATCH payload
  updateSettings: async (id: string, data: SportSettings) => {
    const payload = { settings: toApiTournamentSettings(data) } as unknown as UpdateTournamentRequest;
    const tournament = await apiClient.patch<Tournament>(`/tournaments/${id}`, payload);
    return toSportSettings(tournament.sportType, tournament.settings);
  },

  // ============ TOURNAMENT REGISTRATIONS ============
  
  // Register participant (team or player)
  register: (id: string, data: { participantId: string; participantType: 'team' | 'player'; seed?: number }) =>
    apiClient.post<Registration>(`/tournaments/${id}/register`, data),
  
  // Get tournament registrations
  getRegistrations: (id: string, params?: { page?: number; limit?: number; status?: string; participantType?: string }) =>
    apiClient.get<PaginatedResponse<Registration>>(`/tournaments/${id}/registrations`, { params }),
  
  // ============ TOURNAMENT SEEDING & SCHEDULING ============
  
  // Seed participants
  seed: (id: string, data?: Record<string, unknown>) => apiClient.post<void>(`/tournaments/${id}/seed`, data),
  
  // Generate schedule for specific stage
  generateStageSchedule: (id: string, stageId: string, data?: ScheduleGenerationRequest) =>
    apiClient.post<ScheduleGenerationResponse>(`/tournaments/${id}/stages/${stageId}/generate-schedule`, data ?? {}),

  // Preview schedule for specific stage without persistence
  previewStageSchedule: (id: string, stageId: string, data?: ScheduleGenerationRequest) =>
    apiClient.post<SchedulePreviewResponse>(`/tournaments/${id}/stages/${stageId}/schedule-preview`, data ?? {}),

  // Persist manually adjusted schedule for stage from preview
  saveStageSchedule: (id: string, stageId: string, data: ScheduleSaveRequest) =>
    apiClient.post<ScheduleGenerationResponse>(`/tournaments/${id}/stages/${stageId}/schedule-save`, data),
  
  // ============ TOURNAMENT DATA ============
  
  // Get standings table
  getStandings: (id: string, params?: { stageId?: string }) =>
    apiClient.get<StandingTable>(`/tournaments/${id}/standings`, { params }),

  // ============ TOURNAMENT STAGES ============

  // Get tournament stages
  getStages: (id: string) => apiClient.get<TournamentStage[]>(`/tournaments/${id}/stages`),

  // Create stage in tournament
  createStage: (
    id: string,
    data: {
      name: string;
      orderIndex: number;
      stageType: TournamentStageType;
      status?: TournamentStageStatus;
      active?: boolean;
      settings?: StageSettings;
    }
  ) => apiClient.post<TournamentStage>(`/tournaments/${id}/stages`, data),

  // Update stage in tournament
  updateStage: (
    id: string,
    stageId: string,
    data: Partial<{
      name: string;
      orderIndex: number;
      stageType: TournamentStageType;
      status: TournamentStageStatus;
      active: boolean;
      settings: StageSettings;
    }>
  ) => apiClient.patch<TournamentStage>(`/tournaments/${id}/stages/${stageId}`, data),
  
  // Get playoff bracket
  getBracket: (id: string) => apiClient.get<Bracket>(`/tournaments/${id}/bracket`),
  
  // Get tournament matches
  getMatches: (id: string, params?: { page?: number; limit?: number }) =>
    apiClient.get<PaginatedResponse<Match>>(`/tournaments/${id}/matches`, { params }),
  
  // Create match in tournament
  createMatch: (id: string, data: CreateMatchRequest) =>
    apiClient.post<Match>(`/tournaments/${id}/matches`, data),
  
  // ============ TOURNAMENT RESULTS & STATS ============
  
  // Get tournament results
  getResults: (id: string) => apiClient.get<TournamentResults>(`/tournaments/${id}/results`),
  
  // Get player statistics for tournament
  getPlayerStats: (id: string, params?: { 
    page?: number; 
    limit?: number; 
    teamId?: string; 
    position?: string; 
    from?: string; 
    to?: string; 
    sortBy?: string; 
    order?: 'asc' | 'desc' 
  }) => apiClient.get<PaginatedResponse<PlayerStats>>(`/tournaments/${id}/player-stats`, { params }),
  
  // Get leaderboard
  getLeaderboard: (id: string, params: { 
    metric: string; 
    teamId?: string; 
    position?: string; 
    from?: string; 
    to?: string; 
    limit?: number 
  }) => apiClient.get<LeaderboardPlayers>(`/tournaments/${id}/leaderboards/players`, { params }),
};

// ============ REGISTRATIONS SERVICE ============
export const registrationsService = {
  // Get all registrations with filters
  getAll: (params?: { 
    page?: number; 
    limit?: number; 
    tournamentId?: string; 
    status?: 'pending' | 'approved' | 'rejected'; 
    participantType?: 'team' | 'player' 
  }) => apiClient.get<PaginatedResponse<Registration>>('/registrations', { params }),
  
  // Get registration by ID
  getById: (id: string) => apiClient.get<Registration>(`/registrations/${id}`),
  
  // Update registration status
  update: (id: string, data: Partial<{ status: 'pending' | 'approved' | 'rejected'; seed?: number }>) =>
    apiClient.patch<Registration>(`/registrations/${id}`, data),
  
  // Delete registration
  delete: (id: string) => apiClient.delete<void>(`/registrations/${id}`),
  
  // Restore deleted registration
  restore: (id: string) => apiClient.post<Registration>(`/registrations/${id}/restore`, {}),
};

// ============ MATCHES SERVICE ============
export const matchesService = {
  // Get all matches with filters
  getAll: (params?: { 
    tournamentId?: string; 
    status?: 'scheduled' | 'live' | 'finished' | 'canceled' | 'walkover'; 
    page?: number; 
    limit?: number 
  }) => apiClient.get<PaginatedResponse<Match>>('/matches', { params }),
  
  // Get match by ID
  getById: (id: string) => apiClient.get<Match>(`/matches/${id}`),
  
  // Update match details
  update: (id: string, data: Partial<CreateMatchRequest>) => apiClient.patch<Match>(`/matches/${id}`, data),
  
  // Delete match (soft delete)
  delete: (id: string) => apiClient.delete<void>(`/matches/${id}`),
  
  // Restore deleted match
  restore: (id: string) => apiClient.post<Match>(`/matches/${id}/restore`, {}),
  
  // ============ MATCH EVENTS ============
  
  // Get match events
  getEvents: (id: string) => apiClient.get<MatchEvent[]>(`/matches/${id}/events`),
  
  // Add match event
  addEvent: (id: string, data: CreateMatchEventRequest) =>
    apiClient.post<MatchEvent>(`/matches/${id}/events`, normalizeEventPayload(data)),
  
  // Update match event
  updateEvent: (matchId: string, eventId: string, data: Partial<CreateMatchEventRequest>) =>
    apiClient.patch<MatchEvent>(`/matches/${matchId}/events/${eventId}`, normalizeEventPayload(data)),
  
  // Delete match event
  deleteEvent: (matchId: string, eventId: string) =>
    apiClient.delete<void>(`/matches/${matchId}/events/${eventId}`),
  
  // ============ MATCH RESULTS ============
  
  // Report match result
  reportResult: (id: string, data: ReportResultRequest) =>
    apiClient.post<Match>(`/matches/${id}/report-result`, {
      score: {
        periods: data.periods?.some((period) => period.home || period.away)
          ? data.periods
          : [{ home: data.homeScore, away: data.awayScore }],
      },
    }),
  
  // Confirm match result (by official)
  confirmResult: (id: string) => apiClient.post<Match>(`/matches/${id}/confirm-result`, {}),
};

// ============ STANDINGS SERVICE ============
export const standingsService = {
  // Get standings for tournament
  getByTournament: (tournamentId: string) =>
    apiClient.get<StandingTable>(`/tournaments/${tournamentId}/standings`),
};

// ============ BRACKETS SERVICE ============
export const bracketsService = {
  // Get bracket for tournament
  getByTournament: (tournamentId: string) =>
    apiClient.get<Bracket>(`/tournaments/${tournamentId}/bracket`),
};

// ============ RESULTS SERVICE ============
export const resultsService = {
  // Get tournament results
  getByTournament: (tournamentId: string) =>
    apiClient.get<TournamentResults>(`/tournaments/${tournamentId}/results`),
};

// ============ STATISTICS SERVICE ============
export const statisticsService = {
  // Get player statistics for tournament
  getPlayerStats: (tournamentId: string, params?: {
    page?: number;
    limit?: number;
    teamId?: string;
    position?: string;
    from?: string;
    to?: string;
    sortBy?: string;
    order?: 'asc' | 'desc';
  }) => apiClient.get<PaginatedResponse<PlayerStats>>(`/tournaments/${tournamentId}/player-stats`, { params }),
  
  // Get leaderboard
  getLeaderboard: (tournamentId: string, params: {
    metric: string;
    teamId?: string;
    position?: string;
    from?: string;
    to?: string;
    limit?: number;
  }) => apiClient.get<LeaderboardPlayers>(`/tournaments/${tournamentId}/leaderboards/players`, { params }),
  
  // Get player career/season stats
  getPlayerCareerStats: (playerId: string, params?: {
    scope?: 'career' | 'season' | 'tournament';
    season?: string;
    tournamentId?: string;
  }) => apiClient.get<PlayerStats>(`/players/${playerId}/stats`, { params }),
};
